import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import SpeedIcon from "@mui/icons-material/Speed";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const features = [
  {
    icon: <SearchIcon fontSize="large" />,
    title: "Smart Search Across Millions of Papers",
    description:
      "Our advanced AI algorithms scan through extensive academic databases.",
  },
  {
    icon: <FormatQuoteIcon fontSize="large" />,
    title: "Customizable Outputs",
    description:
      "Choose your preferred citation style, word count, and formatting options.",
  },
  {
    icon: <SpeedIcon fontSize="large" />,
    title: "Save Time, Improve Accuracy",
    description:
      "Automate research compilation while maintaining academic standards.",
  },
  {
    icon: <LibraryBooksIcon fontSize="large" />,
    title: "Own Your References",
    description: "Easily manage and organize your bibliography with our tools.",
  },
];

const LandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Box>
        {/* Hero Section */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.primary.light}25)`,
            pt: { xs: 8, md: 12 },
            pb: { xs: 8, md: 12 },
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2.5rem", md: "3.5rem" },
                    fontWeight: 700,
                    mb: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Effortless Academic Research, Powered by AI
                </Typography>
                <Typography
                  variant="h5"
                  color="text.secondary"
                  sx={{ mb: 4, lineHeight: 1.5 }}
                >
                  Generate comprehensive literature reviews in minutes. From
                  title to thesis, we've got you covered.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/generator")}
                  sx={{
                    py: 2,
                    px: 4,
                    fontSize: "1.1rem",
                    textTransform: "none",
                    borderRadius: 2,
                  }}
                >
                  Start Your Thesis Now
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                {/* Add hero image or animation here */}
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Features Section */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontSize: { xs: "2rem", md: "2.5rem" },
              fontWeight: 700,
              mb: 8,
            }}
          >
            Why Choose Us?
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "translateY(-8px)",
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: "center", p: 3 }}>
                    <IconButton
                      color="primary"
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: theme.palette.primary.main + "15",
                      }}
                    >
                      {feature.icon}
                    </IconButton>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </motion.div>
  );
};

export default LandingPage;
